import { IBottomTreeColumn } from './BottomTree'
import { FlexDiv } from './flexdiv'
import { formatValue } from './InputBox'
import {
  getValueForId,
  haevetPersonligIndkomstId,
  indkomstTilVirksomhedsbeskatningId,
  kapitalAfkastId,
  kontoTilOpsparetOverskudId,
  resterendeOverskudId,
  safeConvertStringToNumber,
  virksomhedsskatId,
} from './VSOSudoku'

interface IVSOTable {
  columns: IBottomTreeColumn[]
  resultatEfterRenterInput: string
  aaretsHaevningerUdgoerInput: string
  hensatTilSenereHaevningPrimoInput: string
  kapitalafkastgrundlagPrimoUdgoerInput: string
  oenskesHaevetPIInput: string
  privateUdlaegUdgoerInput: string
  overfoerselTilMedarbejdendeAegtefaelleInput: string
}

interface INameValue {
  name: string
  value: number
}

function zip<A, B>(a: A[], b: B[]): [A, B][] {
  const length = Math.min(a.length, b.length)
  return Array.from({ length }, (_, i) => [a[i], b[i]])
}

function zipFull<A, B>(a: A[], b: B[]): [A | undefined, B | undefined][] {
  const length = Math.max(a.length, b.length)
  return Array.from({ length }, (_, i) => [a[i] ?? undefined, b[i] ?? undefined])
}

const VSOTable = ({
  columns,
  resultatEfterRenterInput,
  aaretsHaevningerUdgoerInput,
  hensatTilSenereHaevningPrimoInput,
  kapitalafkastgrundlagPrimoUdgoerInput,
  oenskesHaevetPIInput,
  privateUdlaegUdgoerInput,
  overfoerselTilMedarbejdendeAegtefaelleInput,
}: IVSOTable) => {
  const bruttoHaevningList: INameValue[] = []
  let haeveraekkefoelgenList: INameValue[] = []

  const aaretsHaevningerUdgoer = safeConvertStringToNumber(aaretsHaevningerUdgoerInput)
  if (aaretsHaevningerUdgoer !== undefined) {
    bruttoHaevningList.push({
      name: 'Kontant hævet',
      value: aaretsHaevningerUdgoer,
    })
  }

  const virksomhedsskat = safeConvertStringToNumber(getValueForId(columns, virksomhedsskatId))
  if (virksomhedsskat !== undefined) {
    haeveraekkefoelgenList.push({
      name: 'Virksomhedsskat',
      value: virksomhedsskat,
    })
  }
  const haevetPersonligIndkomst = safeConvertStringToNumber(getValueForId(columns, haevetPersonligIndkomstId))
  const indkomstTilVirksomhedsbeskatning = safeConvertStringToNumber(getValueForId(columns, indkomstTilVirksomhedsbeskatningId))
  if (haevetPersonligIndkomst !== undefined && indkomstTilVirksomhedsbeskatning !== undefined) {
    haeveraekkefoelgenList.push({
      name: indkomstTilVirksomhedsbeskatning < 0 ? 'Resterende overskud (for året)' : 'Resterende overskud',
      value: indkomstTilVirksomhedsbeskatning < 0 ? haevetPersonligIndkomst + indkomstTilVirksomhedsbeskatning : haevetPersonligIndkomst,
    })
  }

  const privateUdlaegUdgoer = safeConvertStringToNumber(privateUdlaegUdgoerInput)
  if (privateUdlaegUdgoer !== undefined && privateUdlaegUdgoer > 0) {
    haeveraekkefoelgenList.push({
      name: 'Udlæg',
      value: privateUdlaegUdgoer,
    })
  }

  const htshPrimo = 'HTSH, primo'
  const hensatTilSenereHaevningPrimo = safeConvertStringToNumber(hensatTilSenereHaevningPrimoInput)
  if (hensatTilSenereHaevningPrimo !== undefined && hensatTilSenereHaevningPrimo > 0) {
    haeveraekkefoelgenList.push({
      name: htshPrimo,
      value: hensatTilSenereHaevningPrimo,
    })
  }

  const kapitalafkast = safeConvertStringToNumber(getValueForId(columns, kapitalAfkastId))
  if (kapitalafkast !== undefined && kapitalafkast > 0) {
    haeveraekkefoelgenList.push({
      name: 'Kapitalafkast',
      value: kapitalafkast,
    })
  }

  if (indkomstTilVirksomhedsbeskatning !== undefined && indkomstTilVirksomhedsbeskatning < 0) {
    haeveraekkefoelgenList.push({
      name: 'Konto for opsparet overskud',
      value: -indkomstTilVirksomhedsbeskatning,
    })
  }
  const kontoTilOpsparetOverskud = safeConvertStringToNumber(getValueForId(columns, kontoTilOpsparetOverskudId))
  const resterendeOverskud = safeConvertStringToNumber(getValueForId(columns, resterendeOverskudId))
  if (kontoTilOpsparetOverskud !== undefined && kontoTilOpsparetOverskud < 0) {
    haeveraekkefoelgenList.push({
      name: 'Indskudskontoen',
      value: -kontoTilOpsparetOverskud + (resterendeOverskud ?? 0),
    })
  }

  const sumBruttoHaevning = bruttoHaevningList.reduce((x, y) => x + y.value, 0)
  const sumHaeveraekkefoelgen = haeveraekkefoelgenList.reduce((x, y) => x + y.value, 0)

  if (sumBruttoHaevning <= sumHaeveraekkefoelgen) {
    bruttoHaevningList.push({
      name: 'HTSH, ultimo',
      value: sumHaeveraekkefoelgen - sumBruttoHaevning,
    })
  }

  if (sumHaeveraekkefoelgen < sumBruttoHaevning) {
    haeveraekkefoelgenList = haeveraekkefoelgenList.map(h => {
      if (h.name === htshPrimo) {
        return {
          ...h,
          value: h.value + (sumBruttoHaevning - sumHaeveraekkefoelgen),
        }
      }
      return h
    })
  }

  const zipped = zipFull(bruttoHaevningList, haeveraekkefoelgenList)

  //let rowIndex = 0

  return (
    <FlexDiv>
      <div style={{ width: '100%', border: '1px solid gray', marginTop: 30 }}>
        <div
          style={{
            display: 'table',
            //width: '50%', // 50% af parent
            borderCollapse: 'collapse',
            //border: '2px solid white',
          }}
        >
          <div style={{ display: 'table-row' }}>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: '#85BB24',
                padding: 8,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 200,
              }}
            ></div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: '#85BB24',
                padding: 8,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 200,
              }}
            >
              Bruttohævning
            </div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: '#85BB24',
                padding: 8,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 200,
              }}
            ></div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: '#85BB24',
                padding: 8,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 200,
              }}
            >
              Hæverækkefølgen
            </div>
          </div>

          {zipped.map((z, zIdx) => {
            const bruttoHaevning = z[0]
            const haeveraekkefoelgen = z[1]

            const backgroundColor = zIdx % 2 === 0 ? '#D9E7CD' : '#ECF4E8'

            return (
              <div key={`zbh${zIdx}`} style={{ display: 'table-row' }}>
                <div style={{ display: 'table-cell', border: '1px solid white', backgroundColor, padding: 8, color: 'black', textAlign: 'left' }}>
                  {bruttoHaevning?.name ?? ''}
                </div>
                <div style={{ display: 'table-cell', border: '1px solid white', backgroundColor, padding: 8, color: 'black', textAlign: 'right' }}>
                  {formatValue(`${bruttoHaevning?.value}`) ?? ''}
                </div>
                <div style={{ display: 'table-cell', border: '1px solid white', backgroundColor, padding: 8, color: 'black', textAlign: 'left' }}>
                  {haeveraekkefoelgen?.name ?? ''}
                </div>
                <div style={{ display: 'table-cell', border: '1px solid white', backgroundColor, padding: 8, color: 'black', textAlign: 'right' }}>
                  {formatValue(`${haeveraekkefoelgen?.value}`) ?? ''}
                </div>
              </div>
            )
          })}

          <div style={{ display: 'table-row' }}>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: zipped.length % 2 === 0 ? '#D9E7CD' : '#ECF4E8',
                padding: 8,
                color: 'black',
                textAlign: 'left',
                fontWeight: 'bold',
              }}
            >
              I alt
            </div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: zipped.length % 2 === 0 ? '#D9E7CD' : '#ECF4E8',
                padding: 8,
                color: 'black',
                textAlign: 'right',
                fontWeight: 'bold',
              }}
            >
              {formatValue(`${sumHaeveraekkefoelgen}`)}
            </div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: zipped.length % 2 === 0 ? '#D9E7CD' : '#ECF4E8',
                padding: 8,
                color: 'black',
                textAlign: 'left',
                fontWeight: 'bold',
              }}
            >
              I alt
            </div>
            <div
              style={{
                display: 'table-cell',
                border: '1px solid white',
                backgroundColor: zipped.length % 2 === 0 ? '#D9E7CD' : '#ECF4E8',
                padding: 8,
                color: 'black',
                textAlign: 'right',
                fontWeight: 'bold',
              }}
            >
              {formatValue(`${sumHaeveraekkefoelgen}`)}
            </div>
          </div>
        </div>
      </div>
    </FlexDiv>
  )
}

export default VSOTable
